import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import Navbar from './Navbar';
import { FaCalendarDay, FaEnvelope, FaExclamationTriangle } from 'react-icons/fa';
import { BsCalendarDay, BsCalendarCheck, BsArrowLeft, BsCheckCircle, BsDashCircle } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import { checkAndUpdateEmployeeData } from './employeeUpdates';
import 'react-datepicker/dist/react-datepicker.css';
import './RequestVacations.css';
import { sendVacationRequestNotification } from './emailService';
import NotificationModal from './NotificationModal';

function RequestVacations() {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('checking');
  const [mode, setMode] = useState('full');
  const [employee, setEmployee] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [registeredVacationDays, setRegisteredVacationDays] = useState([]);
  const [overtimeDays, setOvertimeDays] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmButtonEnabled, setConfirmButtonEnabled] = useState(false);
  const [confirmCountdown, setConfirmCountdown] = useState(5);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const timerRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [latestRequest, setLatestRequest] = useState(null);
  const [pendingRequestDays, setPendingRequestDays] = useState([]);

  useEffect(() => {
    // Check pending requests count and fetch employee data when component mounts
    checkPendingRequests();
  }, []);

  const checkPendingRequests = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        // First get the employee data to get the employee ID
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('*')
          .eq('wsp_email', user.email)
          .single();

        if (employeeError) throw employeeError;

        // Count how many pending requests this employee has
        const { data: requests, error: requestsError, count } = await supabase
          .from('vacation_requests')
          .select('id', { count: 'exact' })
          .eq('employee_id', employeeData.id)
          .eq('status', false);

        if (requestsError) throw requestsError;

        setPendingRequestsCount(count || 0);

        // Fetch days from pending requests
        if (requests && requests.length > 0) {
          const requestIds = requests.map(req => req.id);

          // Get all days associated with pending requests
          const { data: pendingDays, error: pendingDaysError } = await supabase
            .from('vacation_requests_days')
            .select('vacation_date')
            .in('request_id', requestIds);

          if (pendingDaysError) throw pendingDaysError;

          // Convert to Date objects
          const pendingDatesObjects = pendingDays ? pendingDays.map(day => {
            return new Date(day.vacation_date + 'T00:00:00');
          }) : [];

          setPendingRequestDays(pendingDatesObjects);
        } else {
          setPendingRequestDays([]);
        }

        // If less than 3 pending requests, proceed with normal flow
        if (count < 3) {
          // Run the employee data through the update algorithm
          const result = await checkAndUpdateEmployeeData([employeeData]);

          // Check if update was successful and we have updated data
          if (result.success && result.updatedEmployees && result.updatedEmployees.length > 0) {
            const updatedEmployee = result.updatedEmployees[0];
            setEmployee(updatedEmployee);

            // Fetch overtime data AFTER the update algorithm has run
            const { data: updatedOvertimeData } = await supabase
              .from('overtime_vacation_days')
              .select('days_remaining')
              .eq('employee_id', updatedEmployee.id)
              .single();

            setOvertimeDays(updatedOvertimeData?.days_remaining);

            // Fetch registered vacation days
            const { data: vacationDaysData } = await supabase
              .from('vacation_days')
              .select('vacation_date')
              .eq('employee_id', updatedEmployee.id);

            // Parse dates
            const registeredDates = vacationDaysData ? vacationDaysData.map(vacation => {
              return new Date(vacation.vacation_date + 'T00:00:00');
            }) : [];

            setRegisteredVacationDays(registeredDates);
            setCurrentView('requestTypeSelection');
          } else {
            console.error('Error updating employee data:', result.errors);
          }
        } else {
          // If 3 or more pending requests, show the maximum limit screen
          setCurrentView('maximumLimitReached');
        }
      }
    } catch (error) {
      console.error('Error checking pending requests:', error);
      setError('Error al cargar datos del empleado.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMode = (selectedMode) => {
    setMode(selectedMode);
    setCurrentView('calendarView');
    setSelectedDates([]);  // Reset selected dates when changing mode
  };

  const isWeekday = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  const handleDateSelect = (date) => {
    if (mode === 'half') {
      // For half-day mode, only allow selecting one date
      setSelectedDates([date]);
    } else {
      // For full-day mode, toggle the date in the selection array
      setSelectedDates(prev => {
        const dateStr = date.toISOString().split('T')[0];
        if (prev.find(d => d.toISOString().split('T')[0] === dateStr)) {
          return prev.filter(d => d.toISOString().split('T')[0] !== dateStr);
        }
        return [...prev, date];
      });
    }
  };

  const renderRequestTypeSelection = () => (
    <div className="request-type-container">
      <h1>Solicitud de Vacaciones</h1>
      <p className="subtitle">Seleccione el tipo de solicitud que desea realizar</p>

      <div className="request-options">
        <div className="request-option">
          <button
            className="request-button"
            onClick={() => handleSelectMode('full')}
          >
            <FaCalendarDay className="request-icon" />
            <span>Solicitar días completos</span>
          </button>
          <p className="request-description">"Me gustaría solicitar uno o mas dias de vacaciones donde me tome el día completo"</p>
        </div>

        <div className="request-option">
          <button
            className="request-button"
            onClick={() => handleSelectMode('half')}
          >
            <BsCalendarDay className="request-icon" />
            <span>Solicitar medio día</span>
          </button>
          <p className="request-description">"Me gustaría pedir la mañana o la tarde de vacaciones de un día en especifico"</p>
        </div>
      </div>
    </div>
  );

  // Add a new useEffect for the confirmation timer
  useEffect(() => {
    if (currentView === 'preConfirmation' && confirmCountdown > 0) {
      timerRef.current = setTimeout(() => {
        setConfirmCountdown(prev => prev - 1);
      }, 1000);

      return () => clearTimeout(timerRef.current);
    } else if (confirmCountdown === 0) {
      setConfirmButtonEnabled(true);
    }
  }, [currentView, confirmCountdown]);

  // Reset countdown when changing views
  useEffect(() => {
    if (currentView === 'preConfirmation') {
      setConfirmCountdown(5);
      setConfirmButtonEnabled(false);
    }
  }, [currentView]);

  // Format date function for the Spanish format
  const formatDateSpanish = (date) => {
    if (!date) return '';

    const options = {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    };

    const formattedDate = date.toLocaleDateString('es-MX', options);

    // Capitalize the first letter of weekday
    return formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
  };

  // Add this new handler
  const handleSubmitRequest = () => {
    setCurrentView('preConfirmation');
  };

  const renderCalendarView = () => (
    <div className="vacation-selection-container">
      <h1>Hola {employee?.full_name}, usted cuenta con {employee?.remaining_days} días disponibles.</h1>
      <p className="overtime-info">
        {overtimeDays !== null
          ? `De los ${employee?.remaining_days} días disponibles, ${overtimeDays} días son de prórroga.`
          : 'Todos sus días disponibles pertenecen al ciclo de vacaciones actual.'}
      </p>
      <div className="vacation-selection-content">
        <div className="selection-counter">
          <span className="counter-label">Has seleccionado</span>
          <span className="counter-number jump-animation" key={`selected-${mode === 'half' ? '0.5' : selectedDates.length}`}>
            {mode === 'half' ? '0.5' : selectedDates.length}
          </span>
          <span className="counter-label">
            {mode === 'half' ? 'de un dia en el calendario' : 'días en el calendario'}
          </span>
        </div>

        <div className="calendar-container">
          <h3>
            {mode === 'half'
              ? '¿Qué día quiere tomarse medio día?'
              : '¿Qué días quiere solicitar?'}
          </h3>
          <DatePicker
            inline
            filterDate={date => {
              // First check if it's a weekday
              const isValidWeekday = isWeekday(date);
              if (!isValidWeekday) return false;
              
              // Check if the day is already taken as vacation
              const isAlreadyTakenVacation = registeredVacationDays.some(registeredDate => 
                registeredDate.toDateString() === date.toDateString()
              );
              if (isAlreadyTakenVacation) return false;
              
              // Check if the day is part of a pending request
              const isInPendingRequest = pendingRequestDays.some(pendingDate => 
                pendingDate.toDateString() === date.toDateString()
              );
              if (isInPendingRequest) return false;
              
              // If passed all checks, the date is valid
              return true;
            }}
            selected={mode === 'half' && selectedDates.length > 0 ? selectedDates[0] : null}
            onChange={handleDateSelect}
            highlightDates={selectedDates}
            multiple={mode === 'full'}
          />
        </div>

        <div className="remaining-counter">
          <span className="counter-label">Le quedarían</span>
          <span className={`counter-number jump-animation ${employee && (employee.remaining_days - (mode === 'half' ? 0.5 : selectedDates.length)) <= 0 ? 'counter-negative' : ''}`} 
                key={`remaining-${employee ? (employee.remaining_days - (mode === 'half' ? 0.5 : selectedDates.length)) : 0}`}>
            {employee ? (employee.remaining_days - (mode === 'half' ? 0.5 : selectedDates.length)) : 0}
          </span>
          <span className="counter-label">días disponibles</span>
        </div>
      </div>

      <div className="button-container">
        <button
          className="submit-button"
          disabled={selectedDates.length === 0}
          onClick={handleSubmitRequest}
        >
          Solicitar <BsCalendarCheck className="button-icon" />
        </button>
        
        <button
          className="back-button"
          onClick={() => setCurrentView('requestTypeSelection')}
        >
          Regresar
        </button>
      </div>
    </div>
  );

  // New function to save the vacation request
  const handleConfirmRequest = async () => {
    setSubmitLoading(true);
    setError(null);

    try {
      // Get current date in Mexico's timezone (America/Mexico_City)
      const today = new Date();
      const mexicoDate = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Mexico_City',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(today);

      // Convert from MM/DD/YYYY to YYYY-MM-DD for Supabase
      const [month, day, year] = mexicoDate.split('/');
      const formattedDate = `${year}-${month}-${day}`;

      // Step 1: Create the vacation request
      const { data: requestData, error: requestError } = await supabase
        .from('vacation_requests')
        .insert([
          {
            request_date: formattedDate,
            status: false, // Pending
            employee_id: employee.id,
            is_half_day: mode === 'half',
            accepted_date: null
          }
        ])
        .select();

      if (requestError) throw requestError;

      // Step 2: Create vacation request days for each selected date
      const requestId = requestData[0].id;
      const daysToInsert = selectedDates.map(date => ({
        request_id: requestId,
        vacation_date: date.toISOString().split('T')[0]
      }));

      const { error: daysError } = await supabase
        .from('vacation_requests_days')
        .insert(daysToInsert);

      if (daysError) throw daysError;

      // If everything is successful, show the confirmation screen
      setCurrentView('confirmationScreen');

    } catch (error) {
      console.error('Error saving vacation request:', error);
      setError('Hubo un error al guardar su solicitud. Por favor intente nuevamente.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Update the fetchCurrentUser function in RequestVacations.js
  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        console.log('Authenticated user:', user);

        // Get employee information by EMAIL, not user_id
        const { data: employeeData, error: employeeError } = await supabase
          .from('employees')
          .select('*')
          .eq('wsp_email', user.email)  // Changed from user_id to wsp_email
          .single();

        if (employeeError) {
          console.error('Error fetching employee by email:', employeeError);
          throw employeeError;
        }

        console.log('Found employee data:', employeeData);
        setCurrentUser(employeeData);

        // Immediately fetch the latest request after setting currentUser
        if (employeeData && currentView === 'confirmationScreen') {
          fetchLatestRequest(employeeData.id);
        }
      }
    } catch (error) {
      console.error('Error fetching current user:', error);
    }
  };

  // Update the fetchLatestRequest function to also get the count of days
  const fetchLatestRequest = async (employeeId) => {
    if (!employeeId) {
      console.error('Cannot fetch latest request: No employee ID provided');
      return;
    }

    try {
      console.log('Fetching latest request for employee ID:', employeeId);

      // First get the request record
      const { data, error } = await supabase
        .from('vacation_requests')
        .select('*')
        .eq('employee_id', employeeId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (error) {
        console.error('Error fetching latest request:', error);
        throw error;
      }

      // Then get the count of days for this request
      if (data) {
        console.log('Found latest request:', data);

        // Get the associated request days
        const { data: requestDays, error: daysError } = await supabase
          .from('vacation_requests_days')
          .select('vacation_date')
          .eq('request_id', data.id);

        if (daysError) {
          console.error('Error fetching request days:', daysError);
        } else {
          console.log('Found request days:', requestDays);

          // Add the total_days property to the request object
          const enhancedRequest = {
            ...data,
            total_days: data.is_half_day ? 0.5 : (requestDays?.length || 1)
          };

          // Also add start_date and end_date for the formatting function
          if (requestDays && requestDays.length > 0) {
            // Sort the dates
            const sortedDates = [...requestDays].sort((a, b) =>
              new Date(a.vacation_date) - new Date(b.vacation_date)
            );

            enhancedRequest.start_date = sortedDates[0].vacation_date;

            if (sortedDates.length > 1) {
              enhancedRequest.end_date = sortedDates[sortedDates.length - 1].vacation_date;
            }
          }

          console.log('Enhanced request with days:', enhancedRequest);
          setLatestRequest(enhancedRequest);
          return;
        }
      }

      // If we couldn't enhance the request, just set it as is
      setLatestRequest(data);
    } catch (error) {
      console.error('Error fetching latest request:', error);
    }
  };

  // Update the useEffect that triggers the currentUser fetch
  useEffect(() => {
    // Fetch user data when the confirmation screen is shown
    if (currentView === 'confirmationScreen') {
      fetchCurrentUser();
    }
  }, [currentView]);

  // Handle email button click
  const handleEmailButtonClick = () => {
    setModalOpen(true);
  };

  // Update the handleSendEmail function
  const handleSendEmail = async (recipientEmails) => {
    console.log('RequestVacations handleSendEmail called with:', recipientEmails);
    console.log('Current user state:', currentUser);
    console.log('Latest request state:', latestRequest);

    // If either is null, try to fetch them again
    if (!currentUser || !latestRequest) {
      console.log('Missing data, attempting to fetch again...');
      fetchCurrentUser();
      return;
    }

    try {
      // Sort dates chronologically
      const sortedDates = [...selectedDates].sort((a, b) => a - b);

      // Format dates as ISO strings (YYYY-MM-DD)
      const formattedDates = sortedDates.map(date =>
        date.toISOString().split('T')[0]
      );

      // Create an enhanced request with guaranteed total_days and individual dates
      const enhancedRequest = {
        ...latestRequest,
        // Force the total_days to match what we expect
        total_days: mode === 'half' ? 0.5 : selectedDates.length,
        // Include all the individual dates
        vacation_dates: formattedDates
      };

      console.log('Sending enhanced request with days:', enhancedRequest);

      // Include the employee's own email in the recipients so they get a confirmation copy
      const allRecipients = [...recipientEmails];
      if (currentUser?.wsp_email && !allRecipients.includes(currentUser.wsp_email)) {
        allRecipients.push(currentUser.wsp_email);
      }

      // Call the function with the enhanced request and all recipients (manager + employee)
      await sendVacationRequestNotification(allRecipients, currentUser, enhancedRequest);
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  // Update the render method for the final confirmation screen
  const renderConfirmationScreen = () => (
    <div className="confirmation-screen">
      <div className="confirmation-content">
        <img src="/WSP_LOGO-05.png" alt="WSP Logo" className="confirmation-logo" />

        <div className="confirmation-message">
          <h1><BsCheckCircle className="confirmation-icon" /> Tu solicitud de vacaciones se ha registrado correctamente</h1>

          <p className="confirmation-instructions">
            Por favor, utiliza el botón "Generar correo" para automaticamente enviar un correo a tu jefe directo. 
            No es necesario que tu se lo envies, nosotros lo haremos por ti, sin embargo, 
            también es buena idea recordarle a tu jefe por Microsoft Teams para que lo revise lo antes posible.
          </p>
        </div>

        <div className="confirmation-buttons">
          <button
            className="email-button"
            onClick={handleEmailButtonClick}
          >
            <FaEnvelope className="button-icon" /> Generar correo
          </button>

          <button
            className="dashboard-button"
            onClick={() => navigate('/dashboard')}
          >
            Regresar a tablero
          </button>
        </div>
      </div>

      {/* Add the notification modal */}
      <NotificationModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        employeeArea={currentUser?.area}
        employeeId={currentUser?.id}
        onSendEmail={handleSendEmail}
      />
    </div>
  );

  // Update the render method for the pre-confirmation view
  const renderPreConfirmationRequest = () => {
    // Sort dates chronologically
    const sortedDates = [...selectedDates].sort((a, b) => a - b);

    return (
      <div className="preconfirmation-container">
        <h1>Confirma nuevamente los días que pedirás en tu solicitud</h1>

        <div className="request-summary">
          <p className="request-type-info">
            {mode === 'half'
              ? 'Está solicitando medio día de vacaciones para la siguiente fecha:'
              : 'Está solicitando los siguientes días de vacaciones:'}
          </p>

          <ul className="dates-list">
            {sortedDates.map((date, index) => (
              <li key={index} className="date-item">
                <span className="date-text">{formatDateSpanish(date)}</span>
                {mode === 'half' && <span className="half-day-badge">Medio día</span>}
              </li>
            ))}
          </ul>

          <div className="vacation-summary">
            <p>Total de días solicitados: <span className="highlight-number">{mode === 'half' ? '0.5' : selectedDates.length}</span></p>
            <p>Días que le quedarán disponibles: <span className={`highlight-number ${employee && (employee.remaining_days - (mode === 'half' ? 0.5 : selectedDates.length)) <= 0 ? 'negative-number' : ''}`}>
              {employee ? (employee.remaining_days - (mode === 'half' ? 0.5 : selectedDates.length)) : 0}
            </span></p>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="confirmation-buttons">
          <button
            className="modify-button"
            onClick={() => setCurrentView('calendarView')}
            disabled={submitLoading}
          >
            <BsArrowLeft className="button-icon" /> Modificar Solicitud
          </button>

          <button
            className={`confirm-button ${!confirmButtonEnabled || submitLoading ? 'disabled' : ''}`}
            disabled={!confirmButtonEnabled || submitLoading}
            onClick={handleConfirmRequest}
          >
            {submitLoading ? (
              'Guardando...'
            ) : confirmButtonEnabled ? (
              <>Confirmar Solicitud <BsCalendarCheck className="button-icon" /></>
            ) : (
              <>Espere {confirmCountdown} segundos...</>
            )}
          </button>
        </div>
      </div>
    );
  };

  // New render function for maximum limit reached view
  const renderMaximumLimitRequests = () => (
    <div className="maximum-limit-container">
      <div className="maximum-limit-content">
        <div className="limit-icon-container">
          <FaExclamationTriangle className="limit-icon" />
        </div>

        <h1>Parece que no puedes crear más solicitudes :(</h1>

        <div className="limit-message">
          <p>
            Los empleados pueden tener un máximo de 3 solicitudes pendientes existentes a la vez,
            esto con la intención de no saturar el sistema, si deseas crear otra solicitud, puedes:
          </p>

          <ul className="limit-options">
            <li>
              <BsDashCircle className="list-icon" />
              Borrar una de tus solicitudes pendientes existentes tu mismo, desde el apartado de
              "Solicitudes" de la barra de navegación.
            </li>
            <li>
              <BsCheckCircle className="list-icon" />
              Esperar a que un jefe directo acepte o decline una de tus solicitudes pendientes,
              si crees que ya pasó una cantidad de tiempo considerable desde que creaste tus solicitudes,
              asegúrate de avisarle a tu jefe directo que las revise lo antes posible.
            </li>
          </ul>
        </div>

        <button
          className="dashboard-button"
          onClick={() => navigate('/dashboard')}
        >
          Regresar al tablero
        </button>
      </div>
    </div>
  );

  return (
    <div className="request-vacations">
      <Navbar />
      <div className={`content ${currentView === 'confirmationScreen' || currentView === 'maximumLimitReached' ? 'confirmation-centered' : ''}`}>
        {loading ? (
          <div className="loading-container">
            <p>Cargando información...</p>
          </div>
        ) : (
          <>
            {currentView === 'checking' && <div className="loading-container"><p>Verificando solicitudes existentes...</p></div>}
            {currentView === 'requestTypeSelection' && renderRequestTypeSelection()}
            {currentView === 'calendarView' && renderCalendarView()}
            {currentView === 'preConfirmation' && renderPreConfirmationRequest()}
            {currentView === 'confirmationScreen' && renderConfirmationScreen()}
            {currentView === 'maximumLimitReached' && renderMaximumLimitRequests()}
          </>
        )}
      </div>
    </div>
  );
}

export default RequestVacations;
